import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  IonContent, IonPage, IonHeader, IonToolbar, IonTitle, IonButtons,
  IonToast, IonLoading
} from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { 
  ChevronLeft, User, Mail, Shield, Crown, TrendingUp, 
  Landmark, Heart, LogOut, Edit3, Save, X, Sparkles,
  Wallet, FileText, Plane, Scale, BookOpen, Loader2,
  RefreshCw, AlertTriangle, Lock, Eye, EyeOff, Fingerprint,Apple
} from 'lucide-react';
import { capacitorDbService } from '../services/capacitorDb.service';
import { useAuthStore } from '../store/authStore';
import { wealthSync } from '../services/wealthSync.service';
import { biometricAuth } from '../services/socialAuth.service';

// ============================================================
// CALCULATIONS
// ============================================================
const calculateRetirementProjection = (plan: any) => {
  if (!plan) return null;
  const yearsToRetirement = plan.retirementAge - plan.currentAge;
  const yearsInRetirement = plan.lifeExpectancy - plan.retirementAge;
  const futureSavings = plan.currentSavings * Math.pow(1 + (plan.expectedReturnRate || 6) / 100, yearsToRetirement);
  const monthlyRate = (plan.expectedReturnRate || 6) / 100 / 12;
  const months = yearsToRetirement * 12;
  const futureContributions = plan.monthlyContribution * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * (1 + monthlyRate);
  const totalProjected = futureSavings + futureContributions;
  const inflatedMonthlyExpenses = (plan.monthlyExpenses || 3500) * Math.pow(1 + (plan.inflationRate || 3.5) / 100, yearsToRetirement);
  const totalNeeded = inflatedMonthlyExpenses * 12 * yearsInRetirement;
  const progressPercent = totalNeeded > 0 ? Math.round((totalProjected / totalNeeded) * 100) : 0;
  return { progressPercent: Math.min(100, progressPercent), onTrack: totalProjected >= totalNeeded };
};

const calculateTakafulScore = (plan: any) => {
  if (!plan) return null;
  const coverageRatio = (plan.existingCoverage || 0) / (plan.recommendedCoverage || 1);
  const progressPercent = Math.min(100, Math.round(coverageRatio * 100));
  return { progressPercent, gap: Math.max(0, (plan.recommendedCoverage || 0) - (plan.existingCoverage || 0)) };
};

const calculateOverallHealth = (retirement: any, takaful: any, assets: any, liabilities: any) => {
  let score = 0;
  let count = 0;
  if (retirement?.progressPercent !== undefined) { score += retirement.progressPercent; count++; }
  if (takaful?.progressPercent !== undefined) { score += takaful.progressPercent; count++; }
  if (assets?.totalValue > 0) { score += Math.min(100, 50 + (assets.totalValue / 100000)); count++; }
  if (liabilities?.totalRemaining > 0) { score += Math.max(0, 100 - (liabilities.totalRemaining / 50000)); count++; }
  return count > 0 ? Math.round(score / count) : 50;
};

const ProfileScreen: React.FC = () => {
  const history = useHistory();
  const { user, setUser, logout, isGuest } = useAuthStore();
  console.log('[ProfileScreen] render auth state:', { userId: user?.id, isGuest });

  const [profile, setProfile] = useState<any>(null);
  const [retirementPlan, setRetirementPlan] = useState<any>(null);
  const [takafulPlan, setTakafulPlan] = useState<any>(null);
  const [assets, setAssets] = useState<any>(null);
  const [liabilities, setLiabilities] = useState<any>(null);
  const [transactions, setTransactions] = useState<any>(null);
  const [zakatRecords, setZakatRecords] = useState<any[]>([]);
  const [hajjPlans, setHajjPlans] = useState<any[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // ============================================================
  // NEW: Biometric states
  // ============================================================
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricType, setBiometricType] = useState<any>('none');
  const [biometricEnabled, setBiometricEnabled] = useState(false);

  // ============================================================
  // FIX: Prevent infinite re-renders
  // Use a ref to track if initial load has happened
  // ============================================================
  const hasInitialLoad = useRef(false);

  // UNIFIED USER ID RESOLUTION
  const getCurrentUserId = useCallback(async (): Promise<string | null> => {
    if (user?.id) {
      console.log('[ProfileScreen] getCurrentUserId from user:', user.id);
      return user.id;
    }
    const globalId = (window as any).__NURWALLET_USER_ID__;
    if (globalId) {
      console.log('[ProfileScreen] getCurrentUserId from globalId:', globalId);
      return globalId;
    }
    try {
      await capacitorDbService.init();
      const guestUser = await capacitorDbService.getUserByEmail('guest@nurwallet.local');
      if (guestUser) {
        (window as any).__NURWALLET_USER_ID__ = guestUser.id;
        return guestUser.id;
      }
    } catch (e) {}
    try {
      const allUsers = await capacitorDbService.getAllUsers?.();
      if (allUsers && allUsers.length > 0) {
        (window as any).__NURWALLET_USER_ID__ = allUsers[0].id;
        return allUsers[0].id;
      }
    } catch (e) {}
    return null;
  }, [user?.id]);

  // ============================================================
  // FIX: loadProfileData — removed user/isGuest/setUser from deps
  // ============================================================
  const loadProfileData = useCallback(async (source: string = 'init') => {
    console.log(`[Profile] Loading data from: ${source}`);
    setIsLoading(true);

    const userId = await getCurrentUserId();
    if (!userId) {
      setIsLoading(false);
      return;
    }

    try {
      await capacitorDbService.init();

      // Load ALL data in parallel
      const [userData, retirement, takaful, assetSummary, liabilitySummary, txSummary, zakat, hajj] = await Promise.all([
        capacitorDbService.getUserById(userId),
        capacitorDbService.getRetirementPlan(userId),
        capacitorDbService.getTakafulPlan(userId),
        capacitorDbService.getAssetSummary(userId).catch(() => ({ totalValue: 0, byType: {} })),
        capacitorDbService.getLiabilitySummary(userId).catch(() => ({ totalRemaining: 0, monthlyPayments: 0, byType: {} })),
        capacitorDbService.getTransactionSummary(userId).catch(() => ({ totalIncome: 0, totalExpense: 0, net: 0 })),
        capacitorDbService.getZakatRecords(userId).catch(() => []),
        capacitorDbService.getHajjPlans(userId).catch(() => []),
      ]);

      setProfile(userData);
      setEditName(userData.fullName || '');
      setRetirementPlan(retirement);
      setTakafulPlan(takaful);
      setAssets(assetSummary);
      setLiabilities(liabilitySummary);
      setTransactions(txSummary);
      setZakatRecords(zakat);
      setHajjPlans(hajj);
      setLastUpdated(new Date());

      // Calculate health score in real-time
      const retirementProj = calculateRetirementProjection(retirement);
      const takafulScore = calculateTakafulScore(takaful);
      const healthScore = calculateOverallHealth(retirementProj, takafulScore, assetSummary, liabilitySummary);

      // FIX: Only update DB, do NOT call setUser() here to avoid render loop
      if (userData.healthScore !== healthScore) {
        const zone: 'CRITICAL' | 'MODERATE' | 'GOOD' = healthScore >= 70 ? 'GOOD' : healthScore >= 40 ? 'MODERATE' : 'CRITICAL';
        await capacitorDbService.updateUser(userId, { 
          healthScore, 
          healthScoreZone: zone as any 
        });
      }

      console.log('[Profile] Data loaded:', {
        retirement: retirement ? 'FOUND' : 'NOT FOUND',
        takaful: takaful ? 'FOUND' : 'NOT FOUND',
        assets: assetSummary?.totalValue || 0,
        liabilities: liabilitySummary?.totalRemaining || 0,
        healthScore,
      });
    } catch (error) {
      console.error('Profile load error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [getCurrentUserId]);

  // ============================================================
  // ALL useEffect HOOKS — TOP LEVEL (React Rules of Hooks)
  // ============================================================

  // 1. Mount effect — run ONCE only
  useEffect(() => {
    if (!hasInitialLoad.current) {
      hasInitialLoad.current = true;
      loadProfileData('mount');
    }
  }, []);

  // 2. Visibility refresh
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        console.log('[Profile] App became visible, refreshing...');
        loadProfileData('visibility');
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [loadProfileData]);

  // 3. Focus refresh
  useEffect(() => {
    const handleFocus = () => {
      console.log('[Profile] Window focused, refreshing...');
      loadProfileData('focus');
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [loadProfileData]);

  // 4. WealthSync event listener
  useEffect(() => {
    const unsubscribe = wealthSync.subscribe(() => {
      console.log('[Profile] WealthSync received, refreshing...');
      loadProfileData('wealthSync');
    });
    return () => unsubscribe();
  }, [loadProfileData]);

  // 5. FIX: Separate effect to sync health score to auth store
  useEffect(() => {
    if (!profile || !user) return;
    
    const retirementProj = calculateRetirementProjection(retirementPlan);
    const takafulScore = calculateTakafulScore(takafulPlan);
    const healthScore = calculateOverallHealth(retirementProj, takafulScore, assets, liabilities);
    
    if (user.healthScore !== healthScore) {
      const zone = healthScore >= 70 ? 'GOOD' : healthScore >= 40 ? 'MODERATE' : 'CRITICAL';
      setUser({ ...user, healthScore, healthScoreZone: zone as any }, isGuest);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, retirementPlan, takafulPlan, assets, liabilities]);

  // ============================================================
  // NEW: Check biometric status on mount
  // ============================================================
  useEffect(() => {
    const checkBio = async () => {
      const available = await biometricAuth.isAvailable();
      setBiometricAvailable(available);
      if (available) {
        const type = await biometricAuth.getBiometryType();
        setBiometricType(type);
        const enabled = await biometricAuth.isBiometricEnabled();
        setBiometricEnabled(enabled);
      }
    };
    checkBio();
  }, []);

  // ============================================================
  // HANDLERS
  // ============================================================
  const handleSaveName = async () => {
    if (!user?.id || !editName.trim()) return;
    try {
      await capacitorDbService.updateUser(user.id, { fullName: editName.trim() });
      const updatedUser = { ...user, fullName: editName.trim() };
      setUser(updatedUser, isGuest);
      (window as any).__NURWALLET_USER_ID__ = updatedUser.id;
      setProfile({ ...profile, fullName: editName.trim() });
      setIsEditing(false);
      showMessage('✅ Name updated successfully');
    } catch (error) {
      showMessage('❌ Failed to update name');
    }
  };

  const handleLogout = async () => {
    await logout();
    (window as any).__NURWALLET_USER_ID__ = null;
    history.replace('/login');
  };

  const showMessage = (msg: string) => {
    setToastMessage(msg);
    setShowToast(true);
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'premium': return 'bg-amber-500 text-white';
      case 'paid': return 'bg-blue-500 text-white';
      default: return 'bg-gray-200 text-gray-600';
    }
  };

  const getHealthScoreColor = (score: number) => {
    if (score >= 70) return 'text-emerald-600';
    if (score >= 40) return 'text-amber-600';
    return 'text-red-600';
  };

  const getHealthScoreBg = (score: number) => {
    if (score >= 70) return 'from-emerald-600 to-teal-600';
    if (score >= 40) return 'from-amber-500 to-orange-500';
    return 'from-red-500 to-red-600';
  };

  // Calculate derived data
  const retirementProj = calculateRetirementProjection(retirementPlan);
  const takafulScore = calculateTakafulScore(takafulPlan);
  const healthScore = calculateOverallHealth(retirementProj, takafulScore, assets, liabilities);

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar style={{ '--background': '#f9fafb' } as React.CSSProperties}>
          <IonButtons slot="start">
            <button 
              onClick={() => history.push('/home')} 
              className="p-2 -ml-2 rounded-full hover:bg-gray-100 transition flex items-center justify-center w-10 h-10"
            >
              <ChevronLeft size={24} className="text-gray-800" />
            </button>
          </IonButtons>
          <IonTitle className="text-lg font-bold">My Profile</IonTitle>
          <IonButtons slot="end">
            <button 
              onClick={() => loadProfileData('manual')} 
              className="p-2 rounded-full hover:bg-gray-100 transition"
              disabled={isLoading}
            >
              <RefreshCw size={18} className={`text-gray-600 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent style={{ '--background': '#f9fafb' } as React.CSSProperties}>
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className="text-emerald-600 animate-spin" />
            <span className="ml-3 text-sm text-gray-500">Loading your data...</span>
          </div>
        ) : !profile ? (
          <div className="flex flex-col items-center justify-center py-20 text-center px-6">
            <User size={48} className="text-gray-300 mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Not Logged In</h2>
            <p className="text-sm text-gray-500 mb-6">Please login to view your profile</p>
            <button
              onClick={() => history.push('/login')}
              className="bg-emerald-600 text-white px-6 py-3 rounded-xl font-bold"
            >
              Go to Login
            </button>
          </div>
        ) : (
          <div className="p-4 space-y-4">
            {/* Profile Card */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                  <User size={32} className="text-emerald-600" />
                </div>
                <div className="flex-1">
                  {isEditing ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-gray-800"
                        autoFocus
                      />
                      <button onClick={handleSaveName} className="p-2 text-emerald-600">
                        <Save size={18} />
                      </button>
                      <button onClick={() => setIsEditing(false)} className="p-2 text-gray-400">
                        <X size={18} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl font-bold text-gray-800">{profile.fullName}</h2>
                      <button onClick={() => setIsEditing(true)} className="p-1 text-gray-400 hover:text-emerald-600">
                        <Edit3 size={14} />
                      </button>
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    <Mail size={14} className="text-gray-400" />
                    <span className="text-sm text-gray-500">{profile.email}</span>
                  </div>
                </div>
              </div>

              {/* Tier Badge */}
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${getTierColor(profile.tier)}`}>
                  {profile.tier?.toUpperCase() || 'FREE'}
                </span>
                {isGuest && (
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-500">
                    Guest
                  </span>
                )}
                {profile.tier === 'premium' && (
                  <span className="flex items-center gap-1 text-xs text-amber-600">
                    <Crown size={14} /> Premium Member
                  </span>
                )}
              </div>
            </div>

            {/* Financial Health Score - REAL TIME */}
            <div className={`bg-gradient-to-r ${getHealthScoreBg(healthScore)} rounded-2xl p-5 text-white`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Heart size={20} className="text-white" />
                  <span className="font-semibold">Financial Health Score</span>
                </div>
                <span className="text-2xl font-bold">{healthScore}%</span>
              </div>
              <div className="w-full h-2 rounded-full bg-white/20 overflow-hidden">
                <div 
                  className="h-full rounded-full bg-white transition-all duration-1000" 
                  style={{ width: `${healthScore}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-white/80">
                  {healthScore >= 70 ? 'Great job! Keep it up.' : 
                   healthScore >= 40 ? 'Needs attention - take action now.' : 
                   'Action required - start planning today.'}
                </p>
                <span className="text-[10px] text-white/60">
                  Updated: {lastUpdated.toLocaleTimeString()}
                </span>
              </div>
            </div>

            {/* Financial Summary Cards */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <Wallet size={16} className="text-emerald-600" />
                  </div>
                  <span className="text-xs text-gray-500">Total Assets</span>
                </div>
                <p className="text-lg font-bold text-gray-800">
                  RM {(assets?.totalValue || 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {Object.keys(assets?.byType || {}).length} categories
                </p>
              </div>
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                    <FileText size={16} className="text-red-500" />
                  </div>
                  <span className="text-xs text-gray-500">Total Liabilities</span>
                </div>
                <p className="text-lg font-bold text-gray-800">
                  RM {(liabilities?.totalRemaining || 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  RM {(liabilities?.monthlyPayments || 0).toLocaleString()}/mo
                </p>
              </div>
            </div>

            {/* Saved Plans - WITH REAL DATA */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Landmark size={18} className="text-emerald-600" />
                My Financial Plans
              </h3>

              <div className="space-y-3">
                {/* Retirement Plan */}
                <button
                  type="button"
                  onClick={() => history.push('/retirement')}
                  className="flex w-full text-left items-center justify-between p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 active:bg-gray-200 transition"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <TrendingUp size={18} className="text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Retirement Plan</p>
                      <p className="text-xs text-gray-500">
                        {retirementProj 
                          ? `${retirementProj.progressPercent}% ready · RM ${(retirementPlan?.monthlyContribution || 0).toLocaleString()}/mo` 
                          : 'Not set up yet'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {retirementProj && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${retirementProj.onTrack ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`}>
                        {retirementProj.onTrack ? 'On Track' : 'Needs Work'}
                      </span>
                    )}
                    <ChevronLeft size={18} className="text-gray-400 rotate-180" />
                  </div>
                </button>

                {/* Takaful Plan */}
                <button
                  type="button"
                  onClick={() => history.push('/takaful')}
                  className="flex w-full text-left items-center justify-between p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 active:bg-gray-200 transition"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                      <Shield size={18} className="text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Family Protection</p>
                      <p className="text-xs text-gray-500">
                        {takafulScore 
                          ? `${takafulScore.progressPercent}% covered · Gap: RM ${takafulScore.gap.toLocaleString()}` 
                          : 'Not set up yet'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {takafulScore && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${takafulScore.progressPercent >= 80 ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>
                        {takafulScore.progressPercent >= 80 ? 'Protected' : 'Gap'}
                      </span>
                    )}
                    <ChevronLeft size={18} className="text-gray-400 rotate-180" />
                  </div>
                </button>

                {/* Zakat Records */}
                <button
                  type="button"
                  onClick={() => history.push('/zakat')}
                  className="flex w-full text-left items-center justify-between p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 active:bg-gray-200 transition"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <Heart size={18} className="text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Zakat Records</p>
                      <p className="text-xs text-gray-500">
                        {zakatRecords.length > 0 
                          ? `${zakatRecords.length} records · Latest: ${zakatRecords[0]?.year}` 
                          : 'No records yet'}
                      </p>
                    </div>
                  </div>
                  <ChevronLeft size={18} className="text-gray-400 rotate-180" />
                </button>

                {/* Hajj Plans */}
                <button
                  type="button"
                  onClick={() => history.push('/hajj')}
                  className="flex w-full text-left items-center justify-between p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 active:bg-gray-200 transition"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                      <Plane size={18} className="text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Hajj Plans</p>
                      <p className="text-xs text-gray-500">
                        {hajjPlans.length > 0 
                          ? `${hajjPlans.length} plans · Target: ${hajjPlans[0]?.targetYear}` 
                          : 'No plans yet'}
                      </p>
                    </div>
                  </div>
                  <ChevronLeft size={18} className="text-gray-400 rotate-180" />
                </button>
              </div>
            </div>

            {/* Cashflow Summary */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <TrendingUp size={18} className="text-emerald-600" />
                Cashflow Summary
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Monthly Income</span>
                  <span className="text-sm font-semibold text-emerald-600">
                    RM {(transactions?.totalIncome || 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Monthly Expenses</span>
                  <span className="text-sm font-semibold text-red-500">
                    RM {(transactions?.totalExpense || 0).toLocaleString()}
                  </span>
                </div>
                <div className="border-t border-gray-100 pt-2 flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Net Savings</span>
                  <span className={`text-sm font-bold ${(transactions?.net || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    RM {(transactions?.net || 0).toLocaleString()}
                  </span>
                </div>
              </div>
              {(transactions?.net || 0) < 0 && (
                <div className="mt-3 p-3 bg-red-50 rounded-xl flex items-center gap-2">
                  <AlertTriangle size={16} className="text-red-500" />
                  <p className="text-xs text-red-600">Your expenses exceed income. Review your budget.</p>
                </div>
              )}
            </div>

            {/* ============================================================ */}
            {/* NEW: Security Section with Biometric Toggle */}
            {/* ============================================================ */}
            {!isGuest && (
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Lock size={18} className="text-emerald-600" />
                  Security
                </h3>

                {/* Biometric Login Toggle */}
                {biometricAvailable && (
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <Fingerprint size={18} className="text-emerald-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">
                          {biometricType === 'faceId' ? 'Face ID' : 
                           biometricType === 'touchId' ? 'Touch ID' : 'Fingerprint'} Login
                        </p>
                        <p className="text-xs text-gray-400">
                          {biometricEnabled ? 'Enabled for quick access' : 'Not set up'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        if (biometricEnabled) {
                          await biometricAuth.disableBiometricLogin();
                          setBiometricEnabled(false);
                          showMessage('✅ Biometric login disabled');
                        } else {
                          const pwd = window.prompt('Enter your current password to enable biometric login:');
                          if (pwd && user?.email) {
                            const result = await biometricAuth.enableBiometricLogin(user.email, pwd);
                            if (result.success) {
                              setBiometricEnabled(true);
                              showMessage('✅ Biometric login enabled!');
                            } else {
                              showMessage('❌ Failed to enable biometric login');
                            }
                          }
                        }
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                        biometricEnabled 
                          ? 'bg-red-50 text-red-600 hover:bg-red-100' 
                          : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
                      }`}
                    >
                      {biometricEnabled ? 'Disable' : 'Enable'}
                    </button>
                  </div>
                )}

                {/* Linked Accounts Info */}
                <div className="py-3">
                  <p className="text-sm font-medium text-gray-700 mb-2">Linked Accounts</p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500 flex items-center gap-2">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Google
                      </span>
                      <span className="text-xs text-gray-400">Not linked</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500 flex items-center gap-2">
                        <Apple size={16} />
                        Apple ID
                      </span>
                      <span className="text-xs text-gray-400">Not linked</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Account Info */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <h3 className="font-bold text-gray-800 mb-4">Account Details</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Account Type</span>
                  <span className="font-medium text-gray-800">
                    {isGuest ? 'Guest (Limited)' : 'Registered User'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Family Status</span>
                  <span className="font-medium text-gray-800">{profile.familyStatus || 'SINGLE'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Member Since</span>
                  <span className="font-medium text-gray-800">
                    {profile.createdAt ? new Date(profile.createdAt).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Last Updated</span>
                  <span className="font-medium text-gray-800">
                    {lastUpdated.toLocaleTimeString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Upgrade Banner (for guests) */}
            {isGuest && (
              <div className="bg-amber-50 rounded-2xl p-5 border border-amber-200">
                <div className="flex items-center gap-3 mb-3">
                  <Sparkles size={20} className="text-amber-600" />
                  <h3 className="font-bold text-amber-800">Create an Account</h3>
                </div>
                <p className="text-sm text-amber-700 mb-4">
                  Register to save your data permanently, access premium features, and track your progress across devices.
                </p>
                <button
                  onClick={() => history.push('/login')}
                  className="w-full bg-amber-500 text-white font-bold py-3 rounded-xl hover:bg-amber-400 transition"
                >
                  Create Free Account
                </button>
              </div>
            )}

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="w-full bg-red-50 border border-red-200 text-red-600 font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 hover:bg-red-100 transition"
            >
              <LogOut size={18} />
              {isGuest ? 'Exit Guest Mode' : 'Log Out'}
            </button>
          </div>
        )}

        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          position="top"
          color={toastMessage.includes('✅') ? 'success' : 'danger'}
        />
      </IonContent>
    </IonPage>
  );
};

export default ProfileScreen;